import {
  CreateMembershipMemberRequest,
  CreateMembershipTierRequest,
  MembershipKpiApi,
  MembershipMemberApi,
  MembershipMemberListResponse,
  MembershipPageMember,
  MembershipViewSource,
  MembershipMemberQueryFilters,
  MembershipTier,
  MembershipTierApi,
  MembershipTierListResponse,
  MembershipTierQueryFilters,
  UpdateMembershipMemberRequest,
  UpdateMembershipMemberStatusRequest,
  UpdateMembershipTierRequest,
  UpdateMembershipTierStatusRequest,
} from '@dataTypes/membership.type'
import { getEnv } from '@utils/env'
import axios from '../axiosConfig'
import {
  mapMembershipKpiFromApi,
  mapMembershipMemberFromApi,
  mapMembershipTierFromApi,
} from './membership.mapper'
import type { MembershipKpiItem } from '@constants/Membership/membershipPage.constants'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_MEMBERSHIP', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export const getAllMembershipTiers = async (
  filters?: MembershipTierQueryFilters,
): Promise<MembershipTierListResponse> => {
  try {
    const response = await axios.get<
      ApiResponse<{ tiers?: MembershipTierApi[]; count?: number }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-tier`, {
      params: filters,
    })

    return {
      tiers: response.data?.data?.tiers || [],
      count: response.data?.data?.count || 0,
    }
  } catch (error) {
    logger.error('Error in getAllMembershipTiers:', error)
    throw error
  }
}

export const getMembershipMemberById = async (
  id: string,
): Promise<MembershipViewSource> => {
  try {
    const response = await axios.get<
      ApiResponse<{ membership_member?: MembershipMemberApi }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-member/${id}`)

    if (response.data?.data?.membership_member) {
      return mapMembershipMemberFromApi(response.data.data.membership_member)
    }

    throw new Error('Failed to retrieve membership member')
  } catch (error) {
    logger.error('Error in getMembershipMemberById:', error)
    throw error
  }
}

export const getMembershipMembers = async (
  filters?: MembershipMemberQueryFilters,
): Promise<MembershipMemberListResponse> => {
  try {
    const response = await axios.get<
      ApiResponse<{ members?: MembershipMemberApi[]; count?: number }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-member`, {
      params: filters,
    })

    const members = (response.data?.data?.members || []).map(
      mapMembershipMemberFromApi,
    )

    return {
      members,
      count: response.data?.data?.count ?? members.length,
    }
  } catch (error) {
    logger.error('Error in getMembershipMembers:', error)
    throw error
  }
}

export const getMembershipMemberKpis = async (filters?: {
  clinic_id?: string
}): Promise<MembershipKpiItem[]> => {
  try {
    const response = await axios.get<
      ApiResponse<{ kpis?: MembershipKpiApi[] }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-member/kpis`, {
      params: filters,
    })

    return (response.data?.data?.kpis || []).map(mapMembershipKpiFromApi)
  } catch (error) {
    logger.error('Error in getMembershipMemberKpis:', error)
    throw error
  }
}

export const createMembershipMember = async (
  payload: CreateMembershipMemberRequest,
): Promise<MembershipPageMember> => {
  try {
    const response = await axios.post<
      ApiResponse<{ membership_member?: MembershipMemberApi }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-member`, payload)

    if (response.data?.data?.membership_member) {
      return mapMembershipMemberFromApi(response.data.data.membership_member)
    }

    throw new Error('Failed to create membership member')
  } catch (error) {
    logger.error('Error in createMembershipMember:', error)
    throw error
  }
}

export const updateMembershipMember = async (
  payload: UpdateMembershipMemberRequest,
): Promise<MembershipViewSource> => {
  try {
    const response = await axios.put<
      ApiResponse<{ membership_member?: MembershipMemberApi }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-member`, payload)

    if (response.data?.data?.membership_member) {
      return mapMembershipMemberFromApi(response.data.data.membership_member)
    }

    throw new Error('Failed to update membership member')
  } catch (error) {
    logger.error('Error in updateMembershipMember:', error)
    throw error
  }
}

export const updateMembershipMemberStatus = async (
  payload: UpdateMembershipMemberStatusRequest,
): Promise<MembershipViewSource> => {
  try {
    const response = await axios.patch<
      ApiResponse<{ membership_member?: MembershipMemberApi }>
    >(
      `${BUSINESS_LAYER_API}/api/v1/membership-member/${payload.membership_member_id}/status`,
      {
        status: payload.status,
        ...(payload.reason ? { reason: payload.reason } : {}),
      },
    )

    if (response.data?.data?.membership_member) {
      return mapMembershipMemberFromApi(response.data.data.membership_member)
    }

    throw new Error('Failed to update membership member status')
  } catch (error) {
    logger.error('Error in updateMembershipMemberStatus:', error)
    throw error
  }
}

export const createMembershipTier = async (
  payload: CreateMembershipTierRequest,
): Promise<MembershipTier> => {
  try {
    const response = await axios.post<
      ApiResponse<{ membership_tier?: MembershipTierApi }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-tier`, payload)

    if (response.data?.data?.membership_tier) {
      return mapMembershipTierFromApi(response.data.data.membership_tier)
    }

    throw new Error('Failed to create membership tier')
  } catch (error) {
    logger.error('Error in createMembershipTier:', error)
    throw error
  }
}

export const updateMembershipTier = async (
  payload: UpdateMembershipTierRequest,
): Promise<MembershipTier> => {
  try {
    const response = await axios.put<
      ApiResponse<{ membership_tier?: MembershipTierApi }>
    >(`${BUSINESS_LAYER_API}/api/v1/membership-tier`, payload)

    if (response.data?.data?.membership_tier) {
      return mapMembershipTierFromApi(response.data.data.membership_tier)
    }

    throw new Error('Failed to update membership tier')
  } catch (error) {
    logger.error('Error in updateMembershipTier:', error)
    throw error
  }
}

export const updateMembershipTierStatus = async (
  payload: UpdateMembershipTierStatusRequest,
): Promise<MembershipTier> => {
  try {
    const response = await axios.patch<
      ApiResponse<{ membership_tier?: MembershipTierApi }>
    >(
      `${BUSINESS_LAYER_API}/api/v1/membership-tier/${payload.membership_tier_id}/status`,
      { enabled: payload.enabled },
    )

    if (response.data?.data?.membership_tier) {
      return mapMembershipTierFromApi(response.data.data.membership_tier)
    }

    throw new Error('Failed to update membership tier status')
  } catch (error) {
    logger.error('Error in updateMembershipTierStatus:', error)
    throw error
  }
}

export const deleteMembershipTier = async (id: string): Promise<void> => {
  try {
    await axios.delete<ApiResponse<Record<string, never>>>(
      `${BUSINESS_LAYER_API}/api/v1/membership-tier/${id}`,
    )
  } catch (error) {
    logger.error('Error in deleteMembershipTier:', error)
    throw error
  }
}

export type {
  CreateMembershipMemberRequest,
  CreateMembershipTierRequest,
  MembershipMemberListResponse,
  MembershipMemberQueryFilters,
  MembershipTierListResponse,
  MembershipTierQueryFilters,
  UpdateMembershipTierRequest,
  UpdateMembershipTierStatusRequest,
} from '@dataTypes/membership.type'
